import { ConfirmationDialog } from 'components/ConfirmationDialog';
import { ControlledDateField, ControlledSelectField, ControlledTextField, ControlNumberField } from 'components/Form';
import type { ConstructionTypeData } from 'gql/graphql';
import { ConstructionTypeIcon } from 'pages/Project/Common/types';
import { Grid, GridItem, IconAlert, type IconComponent, Informer } from 'uikit';

import type React from 'react';
import type { UseFormWatch } from 'react-hook-form';
import type { Control, UseFormClearErrors, UseFormSetValue } from 'react-hook-form/dist/types/form';

import type { IBimActKc2OtherStatusesListItem } from '../types';

import { useBimActKc2StatusChange } from './hooks/useBimActKc2StatusChange';

interface IBimActKc2OtherEditForm {
    isDisabled?: boolean;
    isEditMode?: boolean;
    isReadOnly?: boolean;
    isStatusOnCheckAndHasNoPlanValues?: boolean;
    control: Control;
    watch: UseFormWatch<any>;
    setValue: UseFormSetValue<any>;
    statusItems: IBimActKc2OtherStatusesListItem[];
    seasonsItems: { id: any; label: any }[];
    constructionType?: ConstructionTypeData;
    clearErrors: UseFormClearErrors<any>;
    initialStatusValue?: IBimActKc2OtherStatusesListItem;
    isCorrection?: boolean;
    isInactive?: boolean;
}

const validateDateRange = (value: any[]) => {
    if (!value || value.includes(undefined) || value.includes(null)) {
        return requiredBaseRule.required;
    }

    return true;
};

const requiredBaseRule = { required: 'Поле обязательно для заполнения' };

export const BimActKc2OtherEditForm: React.FC<IBimActKc2OtherEditForm> = (props) => {
    const {
        isDisabled,
        isEditMode,
        isStatusOnCheckAndHasNoPlanValues,
        control,
        statusItems,
        seasonsItems,
        isReadOnly,
        constructionType,
        clearErrors,
        watch,
        setValue,
        initialStatusValue,
        isCorrection,
        isInactive,
    } = props;

    const {
        isOpenedStatusChangeDialog,
        onCloseStatusChangeDialog,
        onActionChangeDialog,
        statusChangeConfirmationData,
    } = useBimActKc2StatusChange({
        control,
        watch,
        setValue,
        initialStatusValue,
    });

    return (
        <>
            <div style={{ padding: 20 }}>
                {isStatusOnCheckAndHasNoPlanValues && (
                    <Informer
                        label="Необходимо заполнить фактические значения хотя бы по одной статье прочих затрат в Акте КС-2"
                        status="warning"
                        icon={IconAlert}
                        style={{ marginBottom: 20 }}
                    />
                )}
                <Grid rowGap="s" cols="1">
                    <GridItem>
                        <ControlledSelectField
                            name="status"
                            label="Статус"
                            control={control}
                            items={statusItems}
                            disabled={isDisabled || !isEditMode || !!isInactive}
                        />
                    </GridItem>
                    <GridItem>
                        <ControlledSelectField
                            name="season"
                            label="Сезон"
                            control={control}
                            items={seasonsItems}
                            disabled={isReadOnly || isEditMode || !!isInactive || isCorrection}
                            rules={requiredBaseRule}
                            required
                        />
                    </GridItem>
                    <GridItem>
                        <ControlledTextField
                            name="group"
                            label="Группа затрат"
                            control={control}
                            type="text"
                            view="clear"
                            value="Прочие"
                            readOnly
                        />
                    </GridItem>
                    <GridItem>
                        <ControlledTextField
                            name="objectName"
                            label="Объект"
                            control={control}
                            type="text"
                            view="clear"
                            readOnly
                        />
                    </GridItem>
                    <GridItem>
                        <ControlledTextField
                            name="constructionType"
                            label="Вид строительства"
                            control={control}
                            type="text"
                            view="clear"
                            readOnly
                            leftSide={
                                constructionType && (ConstructionTypeIcon[constructionType.code] as IconComponent)
                            }
                            iconSize="m"
                        />
                    </GridItem>
                    <GridItem>
                        <div className="ActKC2FieldWrapper">
                            <ControlNumberField
                                name="number"
                                label={isCorrection ? 'Номер исходного Акта КС-2' : 'Номер'}
                                labelPosition="left"
                                control={control}
                                size="s"
                                rules={requiredBaseRule}
                                decimalScale={0}
                                direction="upCenter"
                                disabled={isReadOnly || isCorrection}
                            />
                            <ControlledDateField
                                name="date"
                                label="от"
                                control={control}
                                required
                                disabled={isReadOnly || isCorrection}
                                rules={{
                                    validate: (value) => {
                                        if (!value) {
                                            return 'Поле обязательно для заполнения';
                                        }
                                        clearErrors('date');

                                        return true;
                                    },
                                }}
                            />
                        </div>
                    </GridItem>
                    {isCorrection && (
                        <GridItem>
                            <div className="ActKC2FieldWrapper">
                                <ControlledTextField
                                    name="fullNumber"
                                    label="Номер корректировочного Акта КС-2"
                                    labelPosition="left"
                                    control={control}
                                    size="s"
                                    disabled
                                />
                                <ControlledDateField name="correctionDate" label="от" control={control} />
                            </div>
                        </GridItem>
                    )}
                    <GridItem>
                        <ControlledDateField
                            name="reportingDate"
                            type="date-range"
                            label="Отчетный период"
                            control={control}
                            required
                            rules={{ validate: validateDateRange }}
                            disabled={isReadOnly || isCorrection}
                        />
                    </GridItem>
                    <GridItem>
                        <ControlledTextField
                            name="contractor"
                            label="Подрядчик"
                            control={control}
                            type="text"
                            placeholder="Подрядчик"
                            disabled
                        />
                    </GridItem>
                    <GridItem>
                        <ControlledTextField
                            name="contractorAddress"
                            label="Адрес Подрядчика"
                            control={control}
                            type="text"
                            placeholder="не указан"
                            disabled
                        />
                    </GridItem>
                </Grid>
            </div>
            <ConfirmationDialog
                textHeader="Изменение статуса"
                textConfirmation={statusChangeConfirmationData?.textConfirmationStatusChangeDialog}
                cancelButtonText={statusChangeConfirmationData?.cancelButtonTextStatusChangeDialog || ''}
                confirmButtonText={statusChangeConfirmationData?.confirmButtonTextStatusChangeDialog || ''}
                isOpened={isOpenedStatusChangeDialog}
                onSubmit={onActionChangeDialog}
                onClose={onCloseStatusChangeDialog}
            />
        </>
    );
};

BimActKc2OtherEditForm.displayName = 'BimActKc2OtherEditForm';
